/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { When } from "react-if";
import {
  Button,
  Description,
  FlexContainer,
  Spacer,
  Spinner,
  Text,
  theme,
} from "@nordcloud/gnui";
import { UpdateTurbonomicIntegrationPluginInput } from "~/generated/graphql";
import { EditButton } from "~/components/Buttons";
import { InlineContacts } from "~/components/InlineContacts";
import { isEmpty } from "~/tools";
import { TurbonomicIntegrationPluginProp } from "../../models/IntegrationPlugin";
import { DetailsTitle } from "../DetailsTitle";

type Props = {
  title: string;
  initValues: TurbonomicIntegrationPluginProp["contactPersons"];
  required?: boolean;
  property: string;
  onSubmit: SubmitHandler<UpdateTurbonomicIntegrationPluginInput>;
};

export function InlineContactPicker({
  title,
  initValues,
  required = true,
  property,
  onSubmit,
}: Props) {
  const { handleSubmit, getValues, setValue, register } = useForm();

  const [isEditMode, setIsEditMode] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const doSubmit = async (data: UpdateTurbonomicIntegrationPluginInput) => {
    setIsEditMode(false);
    setLoading(true);
    await onSubmit(data);
    setLoading(false);
  };

  const [contacts, setContacts] = React.useState<string[]>([]);

  const onAdd = (newPerson: string) => {
    const newContacts = [...contacts, newPerson];
    setContacts(newContacts);
    setValue(property, newContacts);
  };

  const onRemove = (deletedPerson: string) => {
    const newContacts = contacts.filter((item) => item !== deletedPerson);
    setContacts(newContacts);
    setValue(property, newContacts);
  };

  const defaultContactIds = initValues.map(({ id }) => id);
  const defaultContactNames = initValues.map(({ name }) => name);

  const EditForm = (
    <form css={{ display: "block" }} onSubmit={handleSubmit(doSubmit)}>
      <input
        type="hidden"
        {...register(property, { required })}
        defaultValue={defaultContactIds}
      />
      <FlexContainer>
        <DetailsTitle>{title}</DetailsTitle>
        <InlineContacts
          contacts={contacts}
          defaultNames={defaultContactNames}
          onAdd={onAdd}
          onRemove={onRemove}
        />
        <Button
          ml={theme.spacing.spacing02}
          type="submit"
          icon="save"
          size="md"
          severity="low"
        >
          Save
        </Button>
        <Button
          ml={theme.spacing.spacing02}
          icon="close"
          type="button"
          size="md"
          severity="low"
          onClick={() => {
            getValues();
            setIsEditMode(false);
          }}
        >
          Cancel
        </Button>
      </FlexContainer>
      <When condition={isEmpty(contacts) && required}>
        <Description>At least one contact person is required.</Description>
      </When>
      <Spacer height={theme.spacing.spacing04} />
    </form>
  );

  if (loading) {
    return (
      <>
        <FlexContainer>
          <DetailsTitle>{title}</DetailsTitle>
          <Spinner size="sm" />
          <Text tag="div" ml={theme.spacing.spacing04}>
            Loading...
          </Text>
        </FlexContainer>
        <Spacer height={theme.spacing.spacing04} />
      </>
    );
  }

  if (isEditMode) {
    return EditForm;
  }

  return (
    <>
      <FlexContainer alignItems="center">
        <DetailsTitle>{title}</DetailsTitle>
        <DisplayText
          content={initValues.map(({ name }) => name).join(", ")}
          onChange={() => {
            setIsEditMode(true);
            setContacts(defaultContactIds);
          }}
        />
      </FlexContainer>
      <Spacer height={theme.spacing.spacing04} />
    </>
  );
}

type DisplayTextProps = {
  content: string;
  onChange: () => void;
};

function DisplayText({ content, onChange }: DisplayTextProps) {
  return (
    <>
      <Text as="div" m="0" mr={theme.spacing.spacing02}>
        {content}
      </Text>
      <EditButton onClick={onChange} />
    </>
  );
}
